// Lógica pura (sin React ni Excel) para el importador de inscripciones.
// El evento entero se importa en UNA sola hoja de datos (todas las
// modalidades juntas); cada fila indica a qué modalidad/categoría
// pertenece mediante un texto libre que se resuelve contra la hoja
// "Modalidad y Categoría". Este módulo expone dos tipos de función:
//   - getAll*: catálogo COMPLETO de campos/productos del evento (sin
//     filtrar por modalidad), usado para generar las columnas de la hoja
//     de datos.
//   - get*ForModalidad: igual que antes, filtrado por modalidad — usado
//     por el validador para saber qué es aplicable/obligatorio en una fila
//     ya resuelta a una modalidad y categoría concretas.

const RAMA_LABELS = { F: "Femenil", V: "Varonil" };

function modalidadCodigo(modalidad) {
  return modalidad.codigoModalidad;
}

function esCampoUtilizable(campo) {
  return (
    campo &&
    campo.tipoCampo !== "titulo" &&
    campo.tipoCampo !== "photo" &&
    campo.iDCampo
  );
}

// Detalle completo de categorías por rama de una modalidad: { rama, categoria
// (código), nombreCategoria, camposAdicionalesKey }. Fuente de verdad para
// construir la hoja "Modalidad y Categoría" y para resolver, durante la
// importación, qué grupo de campos adicionales aplica a cada fila.
export function getCategoriaDetalle(modalidad) {
  const detalle = [];
  const ramaCat = modalidad.ramaCat || {};

  ["rama_F", "rama_V"].forEach((ramaKey) => {
    const ramaObj = ramaCat[ramaKey];
    if (!ramaObj) return;
    const rama = ramaKey.replace("rama_", "");
    const categorias = ramaObj.categorias || {};

    Object.keys(categorias).forEach((catKey) => {
      const categoria = categorias[catKey];
      if (!categoria || categoria.hide) return;
      const codigo =
        categoria.codigo !== undefined
          ? categoria.codigo
          : catKey.replace("categoria_", "");
      const nombreCategoria = categoria.nombreCategoria || catKey;
      detalle.push({
        rama,
        categoria: codigo,
        nombreCategoria,
        camposAdicionalesKey: categoria.camposAdicionalesKey || null,
      });
    });
  });

  return detalle;
}

// ---------------------------------------------------------------------
// Catálogo COMPLETO del evento (todas las modalidades), para generar las
// columnas de la única hoja de datos.
// ---------------------------------------------------------------------

export function getAllGeneralFields(registryConfiguration) {
  const formulario = registryConfiguration.formulario || [];
  return Object.values(formulario).filter(esCampoUtilizable);
}

export function getAllGroupFields(registryConfiguration) {
  const grupos = registryConfiguration.gruposFormularios || {};
  const idsVistos = new Set();
  const campos = [];

  Object.keys(grupos).forEach((grupoKey) => {
    Object.values(grupos[grupoKey] || {}).forEach((campo) => {
      if (!esCampoUtilizable(campo)) return;
      if (idsVistos.has(campo.iDCampo)) return;
      idsVistos.add(campo.iDCampo);
      campos.push({ ...campo, __grupo: grupoKey });
    });
  });

  return campos;
}

export function getAllProducts(registryConfiguration) {
  const productos = registryConfiguration.productosAdicionales || {};
  return Object.values(productos).filter((p) => p && p.codigoProducto !== undefined);
}

// ---------------------------------------------------------------------
// Filtrado por modalidad (usado en validación, no en la generación de la
// hoja): replica exactamente las reglas de visibilidad del formulario real
// de inscripción (ver AditionalInputs.jsx y AditionalProducts.jsx).
// ---------------------------------------------------------------------

export function getGeneralFields(modalidad, registryConfiguration) {
  if (modalidad.hideAditionalInputs) return [];
  const modCode = modalidadCodigo(modalidad);
  return getAllGeneralFields(registryConfiguration).filter(
    (campo) => campo["hide-modalidad_" + modCode] !== true
  );
}

export function getGroupFieldsForModalidad(modalidad, registryConfiguration) {
  const grupos = registryConfiguration.gruposFormularios || {};
  const modCode = modalidadCodigo(modalidad);
  const ramaCat = modalidad.ramaCat || {};

  const gruposUsados = new Set();
  Object.values(ramaCat).forEach((ramaObj) => {
    Object.values((ramaObj && ramaObj.categorias) || {}).forEach((categoria) => {
      if (categoria && categoria.camposAdicionalesKey) {
        gruposUsados.add(categoria.camposAdicionalesKey);
      }
    });
  });

  const idsVistos = new Set();
  const campos = [];

  gruposUsados.forEach((grupoKey) => {
    const grupo = grupos[grupoKey];
    if (!grupo) return;
    Object.values(grupo).forEach((campo) => {
      if (!esCampoUtilizable(campo)) return;
      if (campo["hide-modalidad_" + modCode] === true) return;
      if (idsVistos.has(campo.iDCampo)) return;
      idsVistos.add(campo.iDCampo);
      campos.push({ ...campo, __grupo: grupoKey });
    });
  });

  return campos;
}

export function getProductsForModalidad(modalidad, registryConfiguration) {
  if (modalidad.hideAditionalProducts) return [];
  const modCode = modalidadCodigo(modalidad);
  return getAllProducts(registryConfiguration).filter(
    (producto) => producto["hide-modalidad_" + modCode] !== true
  );
}

function tipoColumna(tipoCampo) {
  if (tipoCampo === "Select") return "select";
  if (tipoCampo === "checkbox") return "checkbox";
  if (tipoCampo === "date") return "date";
  return "text";
}

function campoAColumna(campo, group) {
  const type = tipoColumna(campo.tipoCampo);
  return {
    key: campo.iDCampo,
    fieldKey: campo.iDCampo,
    label: campo.etiqueta || campo.iDCampo,
    type,
    required: campo.validacion === "Requerido",
    group,
    grupoKey: campo.__grupo || null,
    options:
      type === "select"
        ? (campo.valor || []).map((o) => o.texto)
        : type === "checkbox"
        ? ["Sí", "No"]
        : null,
    help: campo.observacion || "",
  };
}

function productoAColumnas(producto) {
  const columnas = [
    {
      key: `producto_${producto.codigoProducto}_cantidad`,
      label: `${producto.nombreProducto} - Cantidad`,
      type: "number",
      required: false,
      group: "producto",
      esCantidadProducto: true,
      productoCodigo: producto.codigoProducto,
      productoNombre: producto.nombreProducto,
      productoDescripcion: producto.descripcionProducto || "",
      precioProducto: producto.precioProducto || 0,
      maxCantidad: producto.maxCantidad,
      help: producto.maxCantidad
        ? `Máximo ${producto.maxCantidad}. Deja en blanco o 0 si el atleta no lo compró.`
        : "Deja en blanco o 0 si el atleta no lo compró.",
    },
  ];

  const formularioProducto = producto.formularioProducto || {};
  Object.values(formularioProducto).forEach((campo) => {
    if (!esCampoUtilizable(campo)) return;
    const columna = campoAColumna(campo, "producto");
    columna.key = `producto_${producto.codigoProducto}_campo_${campo.iDCampo}`;
    columna.label = `${producto.nombreProducto} - ${campo.etiqueta}`;
    columna.productoCodigo = producto.codigoProducto;
    columna.required = false; // solo aplica si la Cantidad del producto es > 0
    columnas.push(columna);
  });

  return columnas;
}

// Columnas fijas, siempre presentes (no dependen de la modalidad).
export function buildFixedColumns(registryConfiguration) {
  const columns = [];
  const separarApellidos = !!(
    registryConfiguration.configuracion &&
    registryConfiguration.configuracion.separarApellidos
  );

  columns.push({ key: "nombres", label: "Nombre(s)", type: "text", required: true, group: "fijo" });

  if (separarApellidos) {
    columns.push({ key: "apellidoPaterno", label: "Apellido Paterno", type: "text", required: true, group: "fijo" });
    columns.push({ key: "apellidoMaterno", label: "Apellido Materno", type: "text", required: false, group: "fijo" });
  } else {
    columns.push({ key: "apellidos", label: "Apellido(s)", type: "text", required: true, group: "fijo" });
  }

  columns.push({
    key: "fechaNacimiento",
    label: "Fecha de nacimiento",
    type: "date",
    required: true,
    group: "fijo",
    help: "Formato AAAA-MM-DD",
  });
  columns.push({ key: "correo", label: "Correo", type: "text", required: true, group: "fijo" });
  columns.push({ key: "telefono", label: "Teléfono", type: "text", required: true, group: "fijo" });

  columns.push({
    key: "modalidadCategoria",
    label: "Modalidad y Categoría",
    type: "text",
    required: true,
    group: "fijo",
    help:
      'Copia exactamente el texto de la columna "Etiqueta" de la hoja "Modalidad y Categoría" para indicar a qué modalidad, género y categoría pertenece esta fila.',
  });

  columns.push({
    key: "numero",
    label: "Número (opcional)",
    type: "number",
    required: false,
    group: "fijo",
    help:
      "Déjalo en blanco para que el sistema asigne el folio automáticamente en un paso posterior. Si lo llenas, se usará ese número.",
  });

  columns.push({
    key: "pagoExterno",
    label: "Pago recibido fuera de Dashport",
    type: "select",
    required: false,
    group: "fijo",
    options: ["No", "Sí"],
    help:
      'Marca "Sí" si el atleta ya pagó por otro medio fuera de Dashport (ej. inscripción gestionada por un tercero). Se agrega un ajuste en la orden para que el monto neto quede en $0, dejando visible el precio original.',
  });

  return columns;
}

// Todas las columnas dinámicas del evento (todas las modalidades juntas),
// para generar la única hoja de datos.
export function buildAllDynamicColumns(registryConfiguration) {
  const columns = [];
  // Un mismo iDCampo puede repetirse entre "general" y distintos grupos (el
  // organizador pudo reusar el mismo nombre de campo en más de un lugar); en
  // ese caso comparten una sola columna en la hoja (igual que ya comparten
  // el mismo atributo al guardarse en la inscripción).
  const clavesVistas = new Set();

  getAllGeneralFields(registryConfiguration).forEach((campo) => {
    if (clavesVistas.has(campo.iDCampo)) return;
    clavesVistas.add(campo.iDCampo);
    columns.push(campoAColumna(campo, "general"));
  });
  getAllGroupFields(registryConfiguration).forEach((campo) => {
    if (clavesVistas.has(campo.iDCampo)) return;
    clavesVistas.add(campo.iDCampo);
    columns.push(campoAColumna(campo, "grupo"));
  });
  getAllProducts(registryConfiguration).forEach((producto) => {
    productoAColumnas(producto).forEach((c) => columns.push(c));
  });

  return columns;
}

// Columnas dinámicas aplicables a UNA modalidad ya resuelta (usado por el
// validador para decidir qué es obligatorio/aplicable en cada fila).
export function getDynamicColumnsForModalidad(modalidad, registryConfiguration) {
  const columns = [];

  getGeneralFields(modalidad, registryConfiguration).forEach((campo) =>
    columns.push(campoAColumna(campo, "general"))
  );
  getGroupFieldsForModalidad(modalidad, registryConfiguration).forEach((campo) =>
    columns.push(campoAColumna(campo, "grupo"))
  );
  getProductsForModalidad(modalidad, registryConfiguration).forEach((producto) => {
    productoAColumnas(producto).forEach((c) => columns.push(c));
  });

  return columns;
}

export { RAMA_LABELS };
